import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { ProtectedRoute } from "./components/layout/ProtectedRoute";
import { BlockedModuleGuard } from "./components/layout/BlockedModuleGuard";
import { ManagerRoute } from "./components/layout/RoleGuard";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { LoadingScreen } from "./components/layout/LoadingScreen";

import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import OrdersPage from "./pages/OrdersPage";
import MenuPage from "./pages/MenuPage";
import TablesPage from "./pages/TablesPage";
import DeliverersPage from "./pages/DeliverersPage";
import TeamPage from "./pages/TeamPage";
import EstafetaApp from "./pages/EstafetaApp";
import ChatBotPage from "./pages/ChatBotPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import StockPage from "./pages/StockPage";
import CustomersPage from "./pages/CustomersPage";
import ReportsPage from "./pages/ReportsPage";
import AIAssistantPage from "./pages/AIAssistantPage";
import AIInsightsPage from "./pages/AIInsightsPage";
import KitchenMonitorPage from "./pages/KitchenMonitorPage";
import KaysouAssistPage from "./pages/KaysouAssistPage";
import TablePaymentThanksPage from "./pages/TablePaymentThanksPage";
import DeliveryMapPage from "./pages/DeliveryMapPage";
import MarketingPage from "./pages/MarketingPage";
import GrowthPage from "./pages/GrowthPage";
import FinancieroPage from "./pages/FinancieroPage";
import PublicMenuPage from "./pages/PublicMenuPage";
import KaisoPrimePage from "./pages/KaisoPrimePage";
import ReservationsPage from "./pages/ReservationsPage";
import ReservationRulesPage from "./pages/ReservationRulesPage";
import ReviewPage from "./pages/ReviewPage";
import RateEstafetaPage from "./pages/RateEstafetaPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import NewOrderPage from "./pages/NewOrderPage";
import CustomerTrackingPage from "./pages/CustomerTrackingPage";
import IntelligencePage from "./pages/IntelligencePage";
import CombosPage from "./pages/CombosPage";
import LeadsPage from "./pages/LeadsPage";
import POSPage from "./pages/POSPage";
import POSTerminalPage from "./pages/POSTerminalPage";
import PlatformAdminPage from "./pages/PlatformAdminPage";
import AffiliatePage from "./pages/AffiliatePage";
import PartnerStartPage from "./pages/PartnerStartPage";
import PontoPage from "./pages/PontoPage";
import PontoFuncionarioPage from "./pages/PontoFuncionarioPage";
import AuditPage from "./pages/AuditPage";
import IntegrationsPage from "./pages/IntegrationsPage";
import InvestorPage from "./pages/InvestorPage";
import AIMarketingPage from "./pages/AIMarketingPage";
import AIMenuEngineeringPage from "./pages/AIMenuEngineeringPage";
import AntiFraudPage from "./pages/AntiFraudPage";
import SmartMenuPage from "./pages/SmartMenuPage";
import KitchenAnalyticsPage from "./pages/KitchenAnalyticsPage";
import PrintStationPage from "./pages/PrintStationPage";
import SupportPage from "./pages/admin/SupportPage";
import LinkPage from "./pages/LinkPage";
import WiFiPortalPage from "./pages/WiFiPortalPage";
import WiFiMarketingPage from "./pages/WiFiMarketingPage";
import SocialConnectPage from "./pages/SocialConnectPage";

import ComoFuncionaPage from "./pages/ComoFuncionaPage";
import PartnersApplyPage from "./pages/PartnersApplyPage";
import RestaurantSignupPage from "./pages/RestaurantSignupPage";
import GuiaPage from "./pages/GuiaPage";
import OwnerRoutes from "./routes/OwnerRoutes";
import PrinterSetupPage from "./pages/PrinterSetupPage";
import PrinterGuidePage from "./pages/PrinterGuidePage";
import ReceiptConfigPage from "./pages/ReceiptConfigPage";
import KaisoBrainPage from "./pages/KaisoBrainPage";
import OportunidadesPage from "./pages/OportunidadesPage";
import SocialAgentPage from "./pages/SocialAgentPage";
import AbandonedOrdersPage from "./pages/AbandonedOrdersPage";
import EventMonitorPage from "./pages/EventMonitorPage";
import CustomDomainMenu, { isCustomDomain } from "./components/CustomDomainMenu";
import MarketplacePage from "./pages/MarketplacePage";
import CustomerPortalPage from "./pages/CustomerPortalPage";
import KarolBannerDemoPage from "./pages/KarolBannerDemoPage";
// ── JAVES — System Health & Incident Command Center ──────────────────────────
const JavesPage = lazy(() => import("./pages/javes/JavesPage"));
const BehavioralCommercePage = lazy(() => import("./pages/BehavioralCommercePage"));
// ─────────────────────────────────────────────────────────────────────────────

// ── NEXUS Module — lazy-loaded & fully isolated from the main bundle ──────────
// All NEXUS components load on-demand. A crash in NEXUS never propagates here.
const NexusErrorBoundary = lazy(() => import("./pages/nexus/NexusErrorBoundary"));
const NexusLayout    = lazy(() => import("./pages/nexus/NexusLayout"));
const NexusDashboard = lazy(() => import("./pages/nexus/NexusDashboard"));
const NexusWorkspace = lazy(() => import("./pages/nexus/NexusWorkspace"));
const NexusProjects  = lazy(() => import("./pages/nexus/NexusProjects"));
const NexusTasks     = lazy(() => import("./pages/nexus/NexusTasks"));
const NexusAgents    = lazy(() => import("./pages/nexus/NexusAgents"));
const NexusFiles     = lazy(() => import("./pages/nexus/NexusFiles"));
const NexusLogs      = lazy(() => import("./pages/nexus/NexusLogs"));
const NexusAnalytics = lazy(() => import("./pages/nexus/NexusAnalytics"));
const NexusSettings  = lazy(() => import("./pages/nexus/NexusSettings"));
const NexusConsole   = lazy(() => import("./pages/nexus/NexusConsole"));
const NexusPreview   = lazy(() => import("./pages/nexus/NexusPreview"));
// ── Studio — produto externo para clientes (via /studio) ─────────────────────
const StudioPage     = lazy(() => import("./pages/studio/StudioPage"));
// ── NEXUS CODE — SaaS externo (nexuscodes.org) ────────────────────────────────
const NexusCodeApp   = lazy(() => import("./pages/nexuscode/NexusCodeApp"));
// ─────────────────────────────────────────────────────────────────────────────
import "./App.css";

/**
 * Detecta se o utilizador está a aceder ao domínio do NEXUS CODE.
 * Produção: nexuscodes.org, www.nexuscodes.org
 * Dev:      REACT_APP_NEXUSCODE=true  ou  ?nexuscode=1 na URL
 */
function isNexusCodeDomain() {
  try {
    const host = window.location.hostname;
    // Produção — domínio oficial nexuscodes.org
    if (host === "nexuscodes.org" || host === "www.nexuscodes.org") return true;
    // Qualquer subdomínio / variante (nexuscode.com, nexuscode.es, staging.nexuscodes.org)
    if (host.includes("nexuscode")) return true;
    // Dev / preview
    if (process.env.REACT_APP_NEXUSCODE === "true") return true;
    if (new URLSearchParams(window.location.search).get("nexuscode") === "1") return true;
    return false;
  } catch { return false; }
}

function App() {
  // Se acedido via nexuscode.es — renderiza app isolada, sem Kaiso
  if (isNexusCodeDomain()) {
    return (
      <ErrorBoundary>
        <Suspense fallback={
          <div style={{ minHeight:"100vh", background:"#030407", display:"flex", alignItems:"center", justifyContent:"center", color:"#64748b", fontSize:13 }}>
            A carregar NEXUS CODE...
          </div>
        }>
          <NexusCodeApp />
        </Suspense>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
    <AuthProvider>
    <SubscriptionProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/karol-demo" element={<KarolBannerDemoPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/link" element={<LinkPage />} />
          <Route path="/affiliate" element={<AffiliatePage />} />
          <Route path="/partners/apply" element={<PartnersApplyPage />} />
          <Route path="/formulario-afiliado" element={<PartnersApplyPage />} />
          <Route path="/partner/start" element={<PartnerStartPage />} />
          <Route path="/signup" element={<RestaurantSignupPage />} />
          <Route path="/m/:restaurantId/product/:productSlug" element={<PublicMenuPage />} />
          <Route path="/m/:restaurantId/oferta/:offerSlug" element={<PublicMenuPage />} />
          <Route path="/m/:restaurantId" element={<PublicMenuPage />} />
          <Route path="/table-payment/thanks" element={<TablePaymentThanksPage />} />
          <Route path="/prime/:restaurantId" element={<KaisoPrimePage />} />
          <Route path="/m" element={<Navigate to="/" replace />} />
          <Route path="/portal/:restaurantId" element={<CustomerPortalPage />} />
          <Route path="/wifi/:restaurantSlug" element={<WiFiPortalPage />} />
          <Route path="/connect/:restaurantSlug" element={<SocialConnectPage />} />
          <Route path="/review/:restaurantId/:orderId" element={<ReviewPage />} />
          <Route path="/rate-estafeta/:restaurantId/:orderId" element={<RateEstafetaPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/track/:orderId" element={<CustomerTrackingPage />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/como-funciona" element={<ComoFuncionaPage />} />
          <Route path="/guia" element={<ProtectedRoute><GuiaPage /></ProtectedRoute>} />
          <Route
            path="/dashboard/behavioral-commerce"
            element={
              <ProtectedRoute>
                <ManagerRoute>
                  <Suspense fallback={<LoadingScreen />}>
                    <BehavioralCommercePage />
                  </Suspense>
                </ManagerRoute>
              </ProtectedRoute>
            }
          />
          <Route path="/behavioral-commerce" element={<Navigate to="/dashboard/behavioral-commerce" replace />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
          <Route path="/orders/new" element={<ProtectedRoute><NewOrderPage /></ProtectedRoute>} />
          <Route path="/menu" element={<ProtectedRoute><MenuPage /></ProtectedRoute>} />
          <Route path="/tables" element={<ProtectedRoute><TablesPage /></ProtectedRoute>} />
          <Route path="/deliverers" element={<ProtectedRoute><DeliverersPage /></ProtectedRoute>} />
          <Route path="/team" element={<ProtectedRoute><ManagerRoute><BlockedModuleGuard module="time_clock"><TeamPage /></BlockedModuleGuard></ManagerRoute></ProtectedRoute>} />
          <Route path="/estafeta" element={<EstafetaApp />} />
          <Route path="/estafeta/*" element={<EstafetaApp />} />
          <Route path="/chatbot" element={<ProtectedRoute><ManagerRoute><ChatBotPage /></ManagerRoute></ProtectedRoute>} />
          <Route path="/invite/:token" element={<AcceptInvitePage />} />
          <Route path="/stock" element={<ProtectedRoute><ManagerRoute><BlockedModuleGuard module="inventory"><StockPage /></BlockedModuleGuard></ManagerRoute></ProtectedRoute>} />
          <Route path="/customers" element={<ProtectedRoute><ManagerRoute><CustomersPage /></ManagerRoute></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute><ManagerRoute><BlockedModuleGuard module="reports"><ReportsPage /></BlockedModuleGuard></ManagerRoute></ProtectedRoute>} />
          <Route path="/ai-assistant" element={<ProtectedRoute><ManagerRoute><BlockedModuleGuard module="ai"><AIAssistantPage /></BlockedModuleGuard></ManagerRoute></ProtectedRoute>} />
          <Route path="/ai-insights" element={<ProtectedRoute><ManagerRoute><BlockedModuleGuard module="ai"><AIInsightsPage /></BlockedModuleGuard></ManagerRoute></ProtectedRoute>} />
          <Route path="/marketing" element={<ProtectedRoute><ManagerRoute><BlockedModuleGuard module="marketing"><MarketingPage /></BlockedModuleGuard></ManagerRoute></ProtectedRoute>} />
          <Route path="/growth" element={<ProtectedRoute><ManagerRoute><BlockedModuleGuard module="marketing"><GrowthPage /></BlockedModuleGuard></ManagerRoute></ProtectedRoute>} />
          <Route path="/intelligence" element={<ProtectedRoute><ManagerRoute><BlockedModuleGuard module="intelligence"><IntelligencePage /></BlockedModuleGuard></ManagerRoute></ProtectedRoute>} />
          <Route path="/ai-marketing" element={<ProtectedRoute><ManagerRoute><AIMarketingPage /></ManagerRoute></ProtectedRoute>} />
          <Route path="/menu-engineering" element={<ProtectedRoute><ManagerRoute><AIMenuEngineeringPage /></ManagerRoute></ProtectedRoute>} />
          <Route path="/antifurto" element={<ProtectedRoute><ManagerRoute><AntiFraudPage /></ManagerRoute></ProtectedRoute>} />
          <Route path="/smart-menu" element={<ProtectedRoute><ManagerRoute><SmartMenuPage /></ManagerRoute></ProtectedRoute>} />
          <Route path="/kitchen-analytics" element={<ProtectedRoute><ManagerRoute><KitchenAnalyticsPage /></ManagerRoute></ProtectedRoute>} />
          <Route path="/combos" element={<ProtectedRoute><BlockedModuleGuard module="combos"><CombosPage /></BlockedModuleGuard></ProtectedRoute>} />
          <Route path="/leads" element={<ProtectedRoute><ManagerRoute><LeadsPage /></ManagerRoute></ProtectedRoute>} />
          <Route path="/financiero" element={<ProtectedRoute><ManagerRoute><BlockedModuleGuard module="financiero"><FinancieroPage /></BlockedModuleGuard></ManagerRoute></ProtectedRoute>} />
          <Route path="/ponto" element={<ProtectedRoute><BlockedModuleGuard module="time_clock"><PontoPage /></BlockedModuleGuard></ProtectedRoute>} />
          <Route path="/ponto-funcionario/:token" element={<PontoFuncionarioPage />} />
          <Route path="/auditoria" element={<ProtectedRoute><ManagerRoute><AuditPage /></ManagerRoute></ProtectedRoute>} />
          <Route path="/integrations" element={<ProtectedRoute><ManagerRoute><IntegrationsPage /></ManagerRoute></ProtectedRoute>} />
          <Route path="/investor/:restaurantId" element={<InvestorPage />} />
          {OwnerRoutes()}
          <Route path="/kitchen" element={<ProtectedRoute><KitchenMonitorPage /></ProtectedRoute>} />
          <Route path="/kds" element={<ProtectedRoute><KitchenMonitorPage /></ProtectedRoute>} />
          <Route path="/kaysou-assist" element={<ProtectedRoute><KaysouAssistPage /></ProtectedRoute>} />
          <Route path="/printer-setup" element={<ProtectedRoute><PrinterSetupPage /></ProtectedRoute>} />
          <Route path="/printer-guide" element={<ProtectedRoute><PrinterGuidePage /></ProtectedRoute>} />
          <Route path="/receipt-config" element={<ProtectedRoute><ReceiptConfigPage /></ProtectedRoute>} />
          <Route path="/print-station" element={<ProtectedRoute><PrintStationPage /></ProtectedRoute>} />
          <Route path="/brain" element={<ProtectedRoute><KaisoBrainPage /></ProtectedRoute>} />
          <Route path="/oportunidades" element={<ProtectedRoute><OportunidadesPage /></ProtectedRoute>} />
          <Route path="/map" element={<ProtectedRoute><DeliveryMapPage /></ProtectedRoute>} />
          <Route path="/pos" element={<ProtectedRoute><POSPage /></ProtectedRoute>} />
          <Route path="/pos-terminal" element={<POSTerminalPage />} />
          <Route path="/platform-admin" element={<PlatformAdminPage />} />
          <Route path="/reservations" element={<ProtectedRoute><ReservationsPage /></ProtectedRoute>} />
          <Route path="/reservation-rules" element={<ProtectedRoute><ReservationRulesPage /></ProtectedRoute>} />
          <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
          <Route path="/event-monitor" element={<ProtectedRoute><EventMonitorPage /></ProtectedRoute>} />
          <Route path="/marketplace" element={<ProtectedRoute><MarketplacePage /></ProtectedRoute>} />
          <Route path="/social-agent" element={<ProtectedRoute><SocialAgentPage /></ProtectedRoute>} />
          <Route path="/abandoned-orders" element={<ProtectedRoute><AbandonedOrdersPage /></ProtectedRoute>} />
          <Route path="/wifi-marketing" element={<ProtectedRoute><ManagerRoute><WiFiMarketingPage /></ManagerRoute></ProtectedRoute>} />

          {/* JAVES — System Health & Incident Command Center */}
          <Route
            path="/javes"
            element={
              <Suspense fallback={
                <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", background:"#0d1117", color:"#475569", fontSize:13 }}>
                  Carregando Javes...
                </div>
              }>
                <JavesPage />
              </Suspense>
            }
          />

          {/*
           * NEXUS — Internal AI Command Center
           * Fully isolated: lazy-loaded, wrapped in its own ErrorBoundary.
           * Any crash inside /nexus/* is caught here and NEVER reaches the
           * main ErrorBoundary or the rest of the application.
           */}
          <Route
            path="/nexus/*"
            element={
              <Suspense fallback={
                <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", background:"#08090d", color:"#475569", fontSize:13 }}>
                  Carregando NEXUS...
                </div>
              }>
                <NexusErrorBoundary>
                  <Routes>
                    <Route element={<NexusLayout />}>
                      <Route index element={<Navigate to="dashboard" replace />} />
                      <Route path="dashboard"  element={<NexusDashboard />} />
                      <Route path="workspace"  element={<NexusWorkspace />} />
                      <Route path="console"    element={<NexusConsole />} />
                      <Route path="preview"    element={<NexusPreview />} />
                      <Route path="projects"   element={<NexusProjects />} />
                      <Route path="tasks"      element={<NexusTasks />} />
                      <Route path="agents"     element={<NexusAgents />} />
                      <Route path="files"      element={<NexusFiles />} />
                      <Route path="logs"       element={<NexusLogs />} />
                      <Route path="analytics"  element={<NexusAnalytics />} />
                      <Route path="settings"   element={<NexusSettings />} />
                    </Route>
                  </Routes>
                </NexusErrorBoundary>
              </Suspense>
            }
          />

          {/* ── Studio — /studio ─────────────────────────────────────────── */}
          <Route
            path="/studio/*"
            element={
              <Suspense fallback={
                <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", background:"#08090d", color:"#475569", fontSize:13 }}>
                  A carregar Studio...
                </div>
              }>
                <ProtectedRoute>
                  <StudioPage />
                </ProtectedRoute>
              </Suspense>
            }
          />

          <Route path="/" element={isCustomDomain() ? <CustomDomainMenu /> : <LandingPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </SubscriptionProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
